import React, { useState } from 'react';
import { Icon, SelectMenu } from '@scale/scaleui';
import _ from 'lodash';
import ArrayBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ArrayBuilder';
import { RoutingDestination, RoutingOverride, RoutingOverrideType } from '@scale/shared-types';
import { RoutingOverrideTypeSchemaMap } from '@scale/shared/pipelinev3/contentUnderstanding/paramsSchema';
import type { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';

interface RoutingOverridesBuilderProps {
  name: string;
  existingValue?: Array<RoutingOverride>;
  updateProperty: (arg: Array<RoutingOverride>) => void;
  required?: boolean;
  typeBuilderMap: TypeBuilderMapType;
}

interface RoutingOverrideOption {
  id: RoutingOverrideType;
  name: string;
}

const routingOverrideOptions: Array<RoutingOverrideOption> = [
  {
    id: RoutingOverrideType.ResponseMatchesValue,
    name: 'Response Matches Value',
  },
  {
    id: RoutingOverrideType.BelowConfidenceThreshold,
    name: 'Below Confidence Threshold',
  },
  {
    id: RoutingOverrideType.TextCollectionGPTOverride,
    name: 'GPT Override',
  },
];

const RoutingOverridesBuilder: React.FC<RoutingOverridesBuilderProps> = ({
  name,
  existingValue = [],
  updateProperty,
  typeBuilderMap,
}) => {
  const routingOverrides = existingValue;
  const [selectedType, setSelectedType] = useState<RoutingOverrideType>(
    RoutingOverrideType.ResponseMatchesValue,
  );

  const addRoutingOverride = (type: RoutingOverrideType) => {
    const newRoutingOverrides = [...routingOverrides];
    newRoutingOverrides.push({
      type,
      enabledSources: [],
      destination: RoutingDestination.L0,
      ...(type === RoutingOverrideType.TextCollectionGPTOverride && {
        modelId: 'gpt_4',
        modelUrl: 'https://api.openai.com/v1/chat/completions',
        modelParams: {
          model: 'gpt-4',
          max_tokens: 1024,
          temperature: 0.5,
          fallbackModel: 'gpt-3.5-turbo',
        },
      }),
    } as any);
    updateProperty(newRoutingOverrides);
  };

  return (
    <div className="scaleui scaleui-tailwind scale-theme">
      {name && <div className="my-2 text-lg">{name}</div>}
      <ArrayBuilder
        typeBuilderMap={typeBuilderMap}
        elements={routingOverrides}
        setElements={updateProperty}
        getKey={routingOverride => JSON.stringify(routingOverride)}
        getTitle={routingOverride => `${_.capitalize(routingOverride.type)}`}
        getSchema={routingOverride => RoutingOverrideTypeSchemaMap[routingOverride.type]}
      />
      <div className="flex">
        <SelectMenu
          className="min-w-[135px]"
          options={routingOverrideOptions}
          selectedOptionId={selectedType}
          setSelected={value => {
            setSelectedType(value as RoutingOverrideType);
          }}
        />
        <Icon
          hasBackground
          icon={['fas', 'plus']}
          onClick={() => addRoutingOverride(selectedType)}
          size="lg"
          variant="primary"
        />
      </div>
    </div>
  );
};

export default RoutingOverridesBuilder;
